import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  Button,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadMain,
  MastheadToggle,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageSidebar,
  PageSidebarBody,
  PageToggleButton,
  SkipToContent,
} from '@patternfly/react-core';
import { BarsIcon } from '@patternfly/react-icons';
import { IAppRoute, IAppRouteGroup, routes } from '@app/routes';
import AdminBadge from './AdminBadge';

interface IAppLayout {
  children: React.ReactNode;
}

const UserPill = (): React.ReactElement | null => {
  const { isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  if (!isSignedIn) {
    return null;
  }

  const label =
    user?.primaryEmailAddress?.emailAddress ||
    user?.username ||
    'Signed in';

  return (
    <div className="user-pill">
      <span className="user-pill__label">{label}</span>
      <Button
        variant="link"
        className="user-pill__logout"
        onClick={() => signOut()}
      >
        Log out
      </Button>
    </div>
  );
};

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState(false);
  const mobileViewRef = React.useRef<boolean | null>(null);
  const location = useLocation();
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handlePageResize = React.useCallback((_event: MouseEvent | TouchEvent | React.KeyboardEvent | undefined, data: any) => {
    if (!data) {
      return;
    }
    const nextMobileView = Boolean(data.mobileView);
    setIsMobile(nextMobileView);

    if (mobileViewRef.current === null || mobileViewRef.current !== nextMobileView) {
      setSidebarOpen(!nextMobileView);
      mobileViewRef.current = nextMobileView;
    }
  }, []);

  const renderNavItem = (route: IAppRoute, index: number) => (
    <NavItem
      key={`${route.label}-${index}`}
      id={`${route.label}-${index}`}
      isActive={route.path === location.pathname}
      to={route.path}
      component={({ className, ...props }) => (
        <NavLink
          {...props}
          to={route.path}
          className={className}
          onClick={() => {
            if (isMobile) {
              setSidebarOpen(false);
            }
          }}
        >
          {route.label}
        </NavLink>
      )}
    />
  );

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => (
    <NavExpandable
      key={`${group.label}-${groupIndex}`}
      id={`${group.label}-${groupIndex}`}
      title={group.label}
      isActive={group.routes.some((route) => route.path === location.pathname)}
    >
      {group.routes.map((route, idx) => route.label && renderNavItem(route, idx))}
    </NavExpandable>
  );

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple" style={{ fontSize: '14px  ', fontWeight: 400 }}>
      {routes.map(
        (route, idx) => route.label && (!route.routes ? renderNavItem(route, idx) : renderNavGroup(route, idx)),
      )}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar
      className={`app-sidebar ${sidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}`}
    >
      <PageSidebarBody>
        <div className="sidebar-header">
          <div className="sidebar-title">DwellBot</div>
        </div>
        {Navigation}
      </PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer?.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );

  const MastheadSection = (
    <Masthead className="app-masthead">
      <MastheadToggle>
        <PageToggleButton
          id="nav-toggle"
          aria-label="Toggle navigation"
          isSidebarOpen={sidebarOpen}
          onSidebarToggle={toggleSidebar}
        >
          <BarsIcon />
        </PageToggleButton>
      </MastheadToggle>
      <MastheadMain>
        <MastheadBrand className="app-masthead-brand">DwellBot</MastheadBrand>
      </MastheadMain>
      <MastheadContent>
        <AdminBadge />
        <UserPill />
      </MastheadContent>
    </Masthead>
  );
  
  return (
    <Page
      masthead={MastheadSection}
      mainContainerId={pageId}
      sidebar={sidebarOpen ? Sidebar : undefined}
      skipToContent={PageSkipToContent}
      onPageResize={handlePageResize}
      isContentFilled={true}
    >
      {children}
    </Page>
  );
};

export { AppLayout };
